<template>
  <div
    :class="[
      'tier-badge',
      `tier-${tierKey}`,
      { 'tier-badge-small': size === 'small', 'tier-badge-large': size === 'large' }
    ]"
    :title="tierName"
  >
    <span class="tier-icon">{{ tierIcon }}</span>
    <span v-if="showName" class="tier-name">{{ tierName }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  // Tier slugs are admin-configurable; unknown slugs fall back to bronze styling
  tierKey: string
  tierName?: string
  tierIcon?: string
  showName?: boolean
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  showName: true,
  size: 'medium'
})

const tierIcon = computed(() => {
  if (props.tierIcon) return props.tierIcon

  const icons: Record<string, string> = {
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  }
  return icons[props.tierKey] || '🥉'
})

const tierName = computed(() => {
  if (props.tierName) return props.tierName

  const names: Record<string, string> = {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum'
  }
  return names[props.tierKey] || 'Bronze'
})
</script>

<style scoped>
.tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.tier-badge-small {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  gap: 0.25rem;
}

.tier-badge-large {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  gap: 0.75rem;
}

.tier-icon {
  font-size: 1.25em;
  line-height: 1;
}

.tier-name {
  line-height: 1;
}

/* Bronze Tier */
.tier-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #b86f28 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.3);
}

.tier-bronze:hover {
  box-shadow: 0 4px 12px rgba(205, 127, 50, 0.4);
  transform: translateY(-1px);
}

/* Silver Tier */
.tier-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%);
  color: #1a1a1a;
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
}

.tier-silver:hover {
  box-shadow: 0 4px 12px rgba(192, 192, 192, 0.5);
  transform: translateY(-1px);
}

/* Gold Tier */
.tier-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
  color: #1a1a1a;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.tier-gold:hover {
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.5);
  transform: translateY(-1px);
}

/* Platinum Tier */
.tier-platinum {
  background: linear-gradient(135deg, #e5e4e2 0%, #b9f2ff 100%);
  color: #1a1a1a;
  box-shadow: 0 2px 8px rgba(185, 242, 255, 0.5), 0 0 20px rgba(185, 242, 255, 0.2);
  animation: platinum-glow 3s ease-in-out infinite;
}

.tier-platinum:hover {
  box-shadow: 0 4px 12px rgba(185, 242, 255, 0.6), 0 0 25px rgba(185, 242, 255, 0.3);
  transform: translateY(-1px);
}

@keyframes platinum-glow {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(185, 242, 255, 0.5), 0 0 20px rgba(185, 242, 255, 0.2);
  }
  50% {
    box-shadow: 0 2px 8px rgba(185, 242, 255, 0.6), 0 0 30px rgba(185, 242, 255, 0.4);
  }
}
</style>
